import type { ReactNode } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import type { RootState } from '../store/store';
import { setAuthData } from '../store/slices/authSlice';
import { engageAuthorization } from '../services/loginService';
import Loader from 'src/components/Loader';

interface ProtectedRouteProps {
  children: ReactNode;
}

const ENGAGE_URL = process.env.REACT_APP_ENGAGE_URL ?? '/';

const redirectToEngage = (): void => {
  window.location.href = ENGAGE_URL;
};

const isTokenExpired = (token: string): boolean => {
  try {
    const { exp } = jwtDecode(token);
    return exp == null || Date.now() / 1000 > exp;
  } catch {
    return true;
  }
};

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { token: reduxToken } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [authState, setAuthState] = useState<'checking' | 'allowed' | 'redirect'>('checking');
  const authAttempted = useRef(false);
  const authCompleted = useRef(false);

  useEffect(() => {
    if (authCompleted.current) return;

    const searchParams = new URLSearchParams(location.search);
    const engageToken = searchParams.get('engageToken') ?? searchParams.get('engagetoken');


    // ─────────────────────────────────────────────
    // PATH A: Session or Token already exists (duplicate tab,
    // new tab, page refresh, internal navigation)
    // ─────────────────────────────────────────────
    const existingSession = sessionStorage.getItem('engageAuthorizationResponse');
    const currentToken = reduxToken ?? localStorage.getItem('jwtToken');

    if ((existingSession !== null && existingSession !== '') || (currentToken !== null && currentToken !== '')) {

      if (currentToken === null || currentToken === '' || isTokenExpired(currentToken)) {
        // Session exists but JWT expired → force fresh login from Engage
        sessionStorage.clear();
        localStorage.clear();
        setAuthState('redirect');
        redirectToEngage();
        return;
      }

      // Valid session → just allow, no redirect needed
      authCompleted.current = true;
      setAuthState('allowed');
      return;
    }

    // ─────────────────────────────────────────────
    // PATH B: No session — must have URL tokens
    // (first load from Engage trigger email)
    // ─────────────────────────────────────────────

    // Both tokens must be present in URL
    if (engageToken === null || engageToken === '') {
      setAuthState('redirect');
      redirectToEngage();
      return;
    }

    // Call API once
    if (authAttempted.current) return;
    authAttempted.current = true;

    const performAuth = async (): Promise<void> => {
      try {
        const response = await engageAuthorization(engageToken);
        console.log(response);
        if (response.token === undefined || response.token === null || response.token === '') {
          setAuthState('redirect');
          redirectToEngage();
          return;
        }

        const { user, token: newToken, role, appConfigs, appRights } = response;

        // Store webhook config
        const webhookConfig = Array.isArray(appConfigs)
          ? (appConfigs as unknown as { configName: string }[]).find(
            (c) => c.configName === 'WebhookURLCount'
          )
          : undefined;
        if (webhookConfig !== undefined && webhookConfig !== null) {
          localStorage.setItem('WebhookURLCount', JSON.stringify(webhookConfig));
        }

        // Persist session
        sessionStorage.setItem('engageAuthorizationResponse', JSON.stringify(response));
        sessionStorage.setItem('engagetoken', engageToken);
        localStorage.setItem('jwtToken', newToken);
        localStorage.setItem('engagetoken', engageToken);

        // Update Redux
        dispatch(setAuthData({
          user,
          token: newToken,
          role,
          superadminClientId: String(user.clientId),
          appConfigs,
          appRights,
        }));

        // Validate the new JWT
        if (isTokenExpired(newToken)) {
          setAuthState('redirect');
          redirectToEngage();
          return;
        }

        // Clean URL and allow in
        authCompleted.current = true;
        navigate('/dashboard', { replace: true });
        setAuthState('allowed');

      } catch {
        setAuthState('redirect');
        redirectToEngage();
      }
    };

    void performAuth();

  }, [reduxToken, navigate, dispatch]);

  const currentToken = reduxToken ?? localStorage.getItem('jwtToken');

  if (authState === 'redirect') return null;

  if (authState === 'checking' || (authState === 'allowed' && (currentToken === null || currentToken === ''))) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader />
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;